'use strict'

import {symbol} from './symbols'

export class GlyphChart {
  constructor(d3, container, Glyph, glyphSize, nodeLimit, charge = -30,
              spawnInterval = 2000, friction = 0.8) {
    this._d3 = d3
    this.container = container
    this.Glyph = Glyph
    this.glyphSize = glyphSize
    this.nodeLimit = nodeLimit
    this.charge = charge
    this.chargeDistance = 50
    this.spawnInterval = spawnInterval
    this.friction = friction

    this.nodes = []
    this.gravity = 0.1

    this.setWidthHeight(container)
    this.createChart()
    this.force = this.initializeForce()
    this.tick()
  }
  setWidthHeight(container) {
    this.width = 100
    this.height = 100 * this.getConatinerAspectRatio(container)
  }
  getConatinerAspectRatio(container) {
    const height = container.getBoundingClientRect().height
    const width = container.getBoundingClientRect().width
    return height / width
  }
  removalDelay(d, i) {
    const delay = 2000 * (i + 1)
    return delay
  }
  tick() {
    setTimeout(this.addNode.bind(this), this.tickInterval())
  }
  tickInterval() {
    return Math.floor(Math.random() * this.spawnInterval)
  }
  moveNodes() {
    this.vis.selectAll('.glyph')
    .attr('transform', glyph => {
      return `translate(${glyph.x}, ${glyph.y}) rotate(${glyph.rotate()})`
    })
  }
  setViewBox() {
    this.vis.attr('viewBox', `0  0 ${this.width} ${this.height}`)
  }
  setPreserveAspectRatio() {
    this.vis.attr('preserveAspectRatio', 'xMinYMin')
  }
  createChart() {
    this.vis = this._d3.select(this.container).insert('svg', ':first-child')
    this.setViewBox()
    this.setPreserveAspectRatio()
  }
  initializeForce() {
    return this._d3.layout.force()
        .nodes(this.nodes)
        .links([])
        .charge(this.charge)
        .chargeDistance(this.chargeDistance)
        .friction(this.friction)
        .gravity(this.gravity)
        .size([this.width, this.height])
        .on('tick', this.moveNodes.bind(this))
        .on('end', this.addNode.bind(this))
  }
  setGlyphStartPosition(glyph) {
    return glyph
  }
  constructGlyphs(glyphs) {
    return glyphs
      .attr('class', glyph => glyph.class)
      .append('path')
      .attr('d', glyph => symbol(1e-6, glyph.type))
      .each(this.setGlyphStartPosition.bind(this))
      .transition()
      .attr('d', glyph => symbol(glyph.size, glyph.type))
  }
  expireGlyphs(glyphs) {
    const removedGlyphs = glyphs
      .transition()
      .delay(this.removalDelay.bind(this))
      .duration(300)

    removedGlyphs.selectAll('path')

      .attr('d', glyph => symbol(1e-6, glyph.type))
      .each('end', () => {
        this.force.nodes().shift()
        this.force.start()
      })

    removedGlyphs.remove()
    return removedGlyphs
  }
  makeGlyphsDraggable(glyphs) {
    glyphs.call(this.force.drag)
  }
  createNode() {
    return new this.Glyph(this.glyphSize)
  }
  addNode() {
    this.tick()
    if (this.nodes.length > this.nodeLimit) {
      return
    }

    this.nodes.push(this.createNode())
    this.force.start()

    const glyphs = this.vis.selectAll('.glyph')
    .data(this.nodes)
    .enter().append('g')

    this.constructGlyphs(glyphs)
    this.expireGlyphs(glyphs)
    this.makeGlyphsDraggable(glyphs)
  }
}

export class ClassedGlyphChart extends GlyphChart {
  constructor(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge = -30,
              spawnInterval = 2000, friction = 0.8) {
    super(d3, container, Glyph, glyphSize, nodeLimit, charge, spawnInterval, friction)
    this.classWeights = classWeights
  }
  createNode() {
    return new this.Glyph(this.glyphSize, this.classWeights)
  }
}

export class MeanderingGlyphChart extends ClassedGlyphChart {
  constructor(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge = -30, spawnInterval = 2000) {
    super(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge, spawnInterval)
    this.gravity = 0
    this.friction = 0.9
    this.chargeDistance = 7
    this.force = super.initializeForce()
  }
  setPreserveAspectRatio() {
    this.vis.attr('preserveAspectRatio', 'xMinYMin slice')
  }
  removalDelay() {
    return 3000
  }
}

export class AttributionTargetChart extends ClassedGlyphChart {
  constructor(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge = -30,
              spawnInterval = 2000, friction = 0.8) {
    super(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge, spawnInterval, friction)
    this.notAnimating = true
    this.minAnimationCount = this.nodeLimit * 0.3
  }
  removalDelay() {
    return 0
  }
  expireGlyphs() {
    const targetedGlyphs = this.vis.selectAll('.glyph--accent:not(.glyph--count)')

    if (targetedGlyphs.data().length > this.minAnimationCount && this.notAnimating) {
      const currentGlyphs = this.vis.selectAll('.glyph')
      this.notAnimating = false
      targetedGlyphs.selectAll('path').classed('glyph--count', true)

      setTimeout(() => {
        const removed = super.expireGlyphs(currentGlyphs)
        this.notAnimating = true
        return removed
      }, 7000)
    }
  }
}

export class MultiFociGlyphChart extends ClassedGlyphChart {
  constructor(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge = -30,
              spawnInterval, friction = 0.8, foci = [{x: 50, y: 50}]) {
    super(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge, spawnInterval, friction)
    this.foci = this.scaleToChartDemensions(foci)
    this.force = super.initializeForce()
  }
  scaleToChartDemensions(focusPoints) {
    return focusPoints.map(point => {
      const x = (point.x / 100) * this.width
      const y = (point.y / 100) * this.height
      return {x, y}
    })
  }
  moveNodes(e) {
    const smoothingFactor = 0.1 * e.alpha
    const foci = this.foci

    this.nodes.forEach(node => {
      node.y += (foci[node.focusId].y - node.y) * smoothingFactor
      node.x += (foci[node.focusId].x - node.x) * smoothingFactor
    })

    super.moveNodes()
  }
}

export class FootTrafficChart extends MultiFociGlyphChart {
  constructor(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge = -30,
              spawnInterval = 2000, friction = 0.8, foci = [{x: 50, y: 50}]) {
    super(d3, container, Glyph, classWeights, glyphSize, nodeLimit, charge,
          spawnInterval, friction, foci)
    this.gravity = 0
    this.force = super.initializeForce()

    window.addEventListener('resize', () => {
      this.setWidthHeight(container)
      this.setViewBox()
      this.foci = this.scaleToChartDemensions(foci)
    })
  }
  setGlyphStartPosition(glyph) {
    const x = this.foci[glyph.focusId].x
    const y = (this.height * 0.9) * -1

    glyph.px = glyph.x = x
    glyph.py = glyph.y = y
    return glyph
  }
  removalDelay() {
    return 4000
  }
}
